import React, { useRef, useState } from 'react';
import useRoute from '@/Hooks/useRoute';
import Button from '@/Components/Buttons/Button';
import FormSection from '@/Blocks/Forms/FormSection';
import Input from '@/Components/Inputs/Input';
import { useToasts } from '@/Hooks/toastContextProvider';
import InputFileUpload from '@/Components/Inputs/InputFileUpload';
import { useForm } from '@inertiajs/react';

export default function UpdateUserLinksInfomationForm({ user }) {
  const route = useRoute();
  const { addToast } = useToasts();
  const [QRCodePreview, setQRCodePreview] = useState({});
  const [landingPagePreview, setLandingPagePreview] = useState({});
  const QRCodeRef = useRef(null);
  const landingPageImageRef = useRef(null);

  const form = useForm({
    _method: 'POST',
    phone: user.phone || '',
    contact_email: user.contact_email || '',
    linkedin_url: user.linkedin_url || '',
    linkedin_qr_code_photo_path: null,
    instagram_url: user.instagram_url || '',
    twitter_url: user.twitter_url || '',
    facebook_url: user.facebook_url || '',
    calendly_url: user.calendly_url || '',
    landing_page_photo_path: user.landing_page_photo_url || '',
    profile_slug: user.profile_slug || '',
  });

  function updateConnectInformation() {
    form.post(route('links.upateConnectInformation'), {
      errorBag: 'upateConnectInformation',
      preserveScroll: true,
      onSuccess: () => handleSuccess(),
    });
  }

  const handleSuccess = () => {
    addToast({ message: 'Updated Successfully', variant: 'success' });
    setQRCodePreview({});
    setLandingPagePreview({});

    if (QRCodeRef.current.value) {
      QRCodeRef.current.value = '';
      form.setData('linkedin_qr_code_photo_path', null);
    }

    if (landingPageImageRef.current.value) {
      landingPageImageRef.current.value = '';
      form.setData('landing_page_photo_path', null);
    }
  };

  function selectNewQRCode() {
    QRCodeRef.current.click();
  }

  function selectNewLandingPagePhoto() {
    landingPageImageRef.current.click();
  }

  function updateQRCodePreview() {
    const QRcode = QRCodeRef.current.files[0];

    if (!QRcode) {
      return;
    }

    form.setData('linkedin_qr_code_photo_path', QRcode);

    const reader = new FileReader();

    reader.onload = e => {
      setQRCodePreview(e.target.result);
    };

    reader.readAsDataURL(QRcode);
  }

  function updateLandingPagePhotoPreview() {
    const landingPageImage = landingPageImageRef.current.files[0];

    if (!landingPageImage) {
      return;
    }

    form.setData('landing_page_photo_path', landingPageImage);

    const reader = new FileReader();

    reader.onload = e => {
      setLandingPagePreview(e.target.result);
    };

    reader.readAsDataURL(landingPageImage);
  }

  return (
    <FormSection
      onSubmit={updateConnectInformation}
      title={'Connect'}
      description={
        <div className="mt-2 flex flex-col justify-between sm:mt-0 sm:flex-row">
          <p className="mb-4">Update your social media profiles and links.</p>
          <a
            href={route('linksProfile.show', user.profile_slug)}
            target="_blank"
          >
            <p className="font-bold text-grey hover:opacity-70">
              View Profile &#8594;
            </p>
          </a>
        </div>
      }
      renderActions={() => (
        <div className="col-span-2">
          <Button
            disabled={form.processing}
            type="submit"
            style="admin"
            variant="dark"
          >
            <p className="w-full text-lg text-white">Save Details</p>
          </Button>
        </div>
      )}
    >
      {/* <!-- Profile Slug --> */}
      <div className="col-span-2 sm:col-span-1">
        <Input
          id="profile_slug"
          label="Profile Slug"
          type="text"
          value={form.data.profile_slug}
          onChange={e =>
            form.setData('profile_slug', e.currentTarget.value.toLowerCase())
          }
          error={form.errors.profile_slug}
        />
      </div>
      {/* <!-- Landing Page Image --> */}
      <div className="col-span-2 sm:col-span-1">
        <InputFileUpload
          type="file"
          className="hidden"
          ref={landingPageImageRef}
          id="landing_page_photo_path"
          onChange={updateLandingPagePhotoPreview}
          onClick={() => selectNewLandingPagePhoto}
          label="Landing Page Photo"
          placeholder={
            Object.keys(landingPagePreview).length === 0 ? (
              <span className="whitespace-nowrap">
                {user.landing_page_photo_url}
              </span>
            ) : (
              <span className="whitespace-nowrap">
                {landingPageImageRef.current.files[0].name}
              </span>
            )
          }
          error={form.errors.landing_page_photo_path}
        />
      </div>
      {/* <!-- Contact Phone Number --> */}
      <div className="col-span-2 sm:col-span-1">
        <Input
          id="phone"
          label="Contact Phone Number"
          type="text"
          value={form.data.phone}
          onChange={e => form.setData('phone', e.currentTarget.value)}
          error={form.errors.phone}
        />
      </div>
      {/* <!-- Contact Email --> */}
      <div className="col-span-2 sm:col-span-1">
        <Input
          label="Contact Email"
          id="contact_email"
          type="email"
          value={form.data.contact_email}
          onChange={e => form.setData('contact_email', e.currentTarget.value)}
          error={form.errors.contact_email}
        />
      </div>
      {/* <!-- LinkedIn Profile URL --> */}
      <div className="col-span-2 sm:col-span-1">
        <Input
          label="LinkedIn Profile URL"
          id="linkedin_url"
          type="text"
          value={form.data.linkedin_url}
          onChange={e => form.setData('linkedin_url', e.currentTarget.value)}
          error={form.errors.email}
        />
      </div>
      {/* <!-- Your Profile QR Code --> */}
      <div className="col-span-2 sm:col-span-1">
        <InputFileUpload
          type="file"
          className="hidden"
          ref={QRCodeRef}
          id="linkedin_qr_code_photo_path"
          onChange={updateQRCodePreview}
          onClick={() => selectNewQRCode}
          label="Your Profile QR Code"
          placeholder={
            Object.keys(QRCodePreview).length === 0 ? (
              <span className="whitespace-nowrap">
                {user.linkedin_qr_code_url}
              </span>
            ) : (
              <span className="whitespace-nowrap">
                {QRCodeRef.current.files[0].name}
              </span>
            )
          }
          error={form.errors.linkedin_qr_code_url}
        />
      </div>
      {/* <!-- Instagram Profile URL--> */}
      <div className="col-span-2 sm:col-span-1">
        <Input
          label="Instagram Profile URL"
          id="instagram_url"
          type="text"
          value={form.data.instagram_url}
          onChange={e => form.setData('instagram_url', e.currentTarget.value)}
          error={form.errors.instagram_url}
        />
      </div>
      {/* <!-- Twitter Profile URL--> */}
      <div className="col-span-2 sm:col-span-1">
        <Input
          label="Twitter Profile URL"
          id="twitter_url"
          type="text"
          value={form.data.twitter_url}
          onChange={e => form.setData('twitter_url', e.currentTarget.value)}
          error={form.errors.twitter_url}
        />
      </div>
      {/* <!-- Facebook Profile URL--> */}
      <div className="col-span-2 sm:col-span-1">
        <Input
          label="Facebook Profile URL"
          id="facebook_url"
          type="text"
          value={form.data.facebook_url}
          onChange={e => form.setData('facebook_url', e.currentTarget.value)}
          error={form.errors.facebook_url}
        />
      </div>
      {/* <!-- Calendly Profile URL--> */}
      <div className="col-span-2 sm:col-span-1">
        <Input
          label="Calendly Profile URL"
          id="calendly_url"
          type="text"
          value={form.data.calendly_url}
          onChange={e => form.setData('calendly_url', e.currentTarget.value)}
          error={form.errors.calendly_url}
        />
      </div>
    </FormSection>
  );
}
